import React, { useMemo, useState } from "react";
import {
  ContactBatch,
  PatchableContactFields,
} from "../../../../services/contactImportService";

interface ContactReviewCardProps {
  batch: ContactBatch;
  onPatchContact: (index: number, fields: PatchableContactFields) => Promise<void>;
  /** Selected indices are the ones the caller should send as contact_indices to /contacts/upsert. */
  onImport: (selectedIndices: number[], tags: string[]) => Promise<void>;
  isImporting?: boolean;
  importError?: string | null;
}

type Draft = { name: string; company: string; email: string; phone: string };

const issueBadge: Record<string, { label: string; tone: "warn" | "danger" }> = {
  // Backend upsert hard-rejects a contact with neither email nor phone
  // (contact_ingest/app.py's _upsert_contacts: "no_email_or_phone" is
  // filtered before it ever reaches GHL) — danger, not warn, and unlike
  // invalid_email/invalid_phone this one blocks selection entirely below.
  no_email_or_phone: { label: "Can't import", tone: "danger" },
  invalid_email: { label: "Invalid email", tone: "danger" },
  invalid_phone: { label: "Invalid phone", tone: "danger" },
};

/** UpsertResult.error is either a "ghl_status_NNN" code or, occasionally, an
 *  already-readable backend string — cover the common codes, fall back to
 *  showing the raw reason so a new/unmapped code is never silently blank. */
const upsertErrorLabel = (reason: string): string => {
  const statusMatch = reason.match(/^ghl_status_(\d+)$/);
  if (statusMatch) {
    const status = statusMatch[1];
    if (status === "422") return "GHL rejected this contact's data";
    if (status === "401" || status === "403") return "GHL connection needs reauthorization";
    if (status === "429") return "GHL rate limit — try again shortly";
    return `GHL error (${status})`;
  }
  return reason || "Import failed";
};

const badgeStyle = (tone: "warn" | "danger" | "ok" | "crm"): React.CSSProperties => {
  if (tone === "danger") {
    return {
      background: "color-mix(in srgb, var(--color-danger) 16%, var(--color-surface))",
      color: "var(--color-danger)",
    };
  }
  if (tone === "warn") {
    return {
      background: "color-mix(in srgb, var(--color-accent-warm) 24%, var(--color-surface))",
      color: "color-mix(in srgb, var(--color-accent-warm) 60%, var(--color-text-primary) 40%)",
    };
  }
  return {
    background: "color-mix(in srgb, var(--color-primary) 10%, transparent)",
    color: "var(--color-primary)",
  };
};

const rowAccentStyle = (tone: "ok" | "crm" | "warn" | "danger" | null): React.CSSProperties => {
  if (!tone) return {};
  const color =
    tone === "danger"
      ? "var(--color-danger)"
      : tone === "warn"
      ? "var(--color-accent-warm)"
      : tone === "crm"
      ? "color-mix(in srgb, var(--color-text-secondary) 40%, transparent)"
      : "color-mix(in srgb, var(--color-primary) 55%, transparent)";
  return { borderLeftWidth: 3, borderLeftColor: color };
};

const avatarTint = (i: number): React.CSSProperties => {
  const variants: React.CSSProperties[] = [
    { background: "color-mix(in srgb, var(--color-primary) 16%, transparent)", color: "var(--color-primary)" },
    { background: "color-mix(in srgb, var(--color-secondary) 18%, transparent)", color: "var(--color-primary)" },
    {
      background: "color-mix(in srgb, var(--color-accent-warm) 24%, transparent)",
      color: "color-mix(in srgb, var(--color-accent-warm) 55%, var(--color-text-primary) 45%)",
    },
  ];
  return variants[i % variants.length];
};

const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("") || "?";

// At the card's real embedded width (as little as ~360px total), 4 side-by-side
// text columns can't all show meaningful content — the status badge's own
// min-content width alone eats ~80px, and name/email/phone are left splitting
// what's left three ways down to single-digit pixels each. Name and phone are
// what a person needs to recognize/verify a row at a glance, so they're
// stacked as two full-width lines in one column instead of three columns
// splitting the same space horizontally; company/email fold into a smaller
// third line (still complete on hover via title, same as the rest). Status
// badge + checkbox + edit/delete share one merged trailing column (rather
// than two separate tracks) so every pixel not spent there goes to the
// name/phone column instead.
const ROW_GRID = "grid-cols-[32px_1fr_auto]";
const COMPACT_GRID = "grid-cols-[32px_1fr_auto]";

/** Inline chat-native contact review (SGL-81) — the whole review (list, edit,
 *  drop, tag, import) lives in this one card in the thread. No side panel. */
export const ContactReviewCard: React.FC<ContactReviewCardProps> = ({
  batch,
  onPatchContact,
  onImport,
  isImporting = false,
  importError,
}) => {
  // Two distinct, independent actions: unchecking a box just excludes a
  // contact from THIS import (row stays visible, normal); deleting removes
  // it from the list entirely (struck through, with Undo). Unchecking must
  // never look or behave like deleting.
  //
  // invalid_email/invalid_phone rows start unchecked (opt-in, not opt-out):
  // the row still has SOME value in that field, so unlike no_email_or_phone
  // it isn't hard-blocked from selection — but importing it as-is would
  // silently overwrite a real GHL value with garbage. Badging it "Invalid
  // Phone" isn't enough on its own; the card's whole premise ("fix typos...
  // before anything writes to GHL") means bad data must never be pre-checked.
  const [unchecked, setUnchecked] = useState<Set<number>>(
    () =>
      new Set(
        batch.contacts
          .map((c, i) => i)
          .filter((i) => batch.contacts[i].issues.some((iss) => iss === "invalid_email" || iss === "invalid_phone")),
      ),
  );
  const [deleted, setDeleted] = useState<Set<number>>(new Set());
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [draft, setDraft] = useState<Draft>({ name: "", company: "", email: "", phone: "" });
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  // Pre-filled from the backend's Bedrock suggestion (SGL-81) so the user
  // edits/removes it instead of typing from scratch — never applied without
  // their own Import click either way, so a bad or missing suggestion just
  // means an empty/different starting chip, nothing more.
  const [tags, setTags] = useState<string[]>(batch.suggested_tag ? [batch.suggested_tag] : []);
  const [tagInput, setTagInput] = useState("");
  const [isCollapsed, setIsCollapsed] = useState(false);

  const contacts = batch.contacts;

  const { selectableIndices, selectedIndices, blockedCount } = useMemo(() => {
    const selectable = contacts
      .map((c, i) => i)
      .filter(
        (i) =>
          contacts[i].status !== "duplicate_in_batch" &&
          !contacts[i].upserted &&
          !contacts[i].issues.includes("no_email_or_phone") &&
          !deleted.has(i),
      );
    return {
      selectableIndices: selectable,
      selectedIndices: selectable.filter((i) => !unchecked.has(i)),
      blockedCount: contacts.filter((c) => c.issues.includes("no_email_or_phone")).length,
    };
  }, [contacts, deleted, unchecked]);

  const uncheckedCount = selectableIndices.length - selectedIndices.length;
  const deletedCount = deleted.size;
  const duplicateCount = contacts.filter((c) => c.status === "duplicate_in_batch").length;
  const allSelected = selectableIndices.length > 0 && uncheckedCount === 0;

  const toggleChecked = (i: number) => {
    setUnchecked((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setUnchecked(allSelected ? new Set(selectableIndices) : new Set());
  };

  const deleteRow = (i: number) => {
    setDeleted((prev) => new Set(prev).add(i));
    setUnchecked((prev) => {
      if (!prev.has(i)) return prev;
      const next = new Set(prev);
      next.delete(i);
      return next;
    });
  };

  const undoDelete = (i: number) => {
    setDeleted((prev) => {
      const next = new Set(prev);
      next.delete(i);
      return next;
    });
  };

  const startEdit = (i: number) => {
    const c = contacts[i];
    setDraft({ name: c.name, company: c.company, email: c.email, phone: c.phone });
    setEditingIndex(i);
  };
  const cancelEdit = () => setEditingIndex(null);

  const saveEdit = async (i: number) => {
    const original = contacts[i];
    const fields: PatchableContactFields = {};
    (Object.keys(draft) as (keyof Draft)[]).forEach((key) => {
      if (draft[key] !== original[key]) fields[key] = draft[key];
    });
    if (Object.keys(fields).length === 0) {
      setEditingIndex(null);
      return;
    }
    setIsSavingEdit(true);
    try {
      await onPatchContact(i, fields);
      setEditingIndex(null);
    } finally {
      setIsSavingEdit(false);
    }
  };

  const addTag = () => {
    const value = tagInput.trim();
    if (value && !tags.includes(value)) setTags((prev) => [...prev, value]);
    setTagInput("");
  };
  const removeTag = (tag: string) => setTags((prev) => prev.filter((t) => t !== tag));

  if (isCollapsed) {
    return (
      <button
        type="button"
        onClick={() => setIsCollapsed(false)}
        className="group my-1 flex w-full max-w-full items-center justify-between gap-3 rounded-2xl border border-theme-border bg-theme-surface px-4 py-3 text-left transition-colors hover:border-theme-primary/35"
      >
        <div className="flex min-w-0 items-center gap-3">
          <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-theme-secondary/20">
            <span className="material-icons-outlined text-[18px] text-theme-primary">group</span>
          </span>
          <div className="min-w-0">
            <p className="text-[13px] font-bold text-theme-fg">Review Contacts &mdash; {contacts.length} total</p>
            <p className="truncate text-[11px] text-theme-fg-muted">
              {selectedIndices.length} of {selectableIndices.length} selected &middot; collapsed
            </p>
          </div>
        </div>
        <span className="flex flex-shrink-0 items-center gap-1 text-[11px] font-bold text-theme-primary">
          Expand<span className="material-icons-outlined text-[16px]">unfold_more</span>
        </span>
      </button>
    );
  }

  return (
    <div className="my-1 w-full max-w-full overflow-hidden rounded-[20px] border border-theme-border bg-theme-surface shadow-[0_1px_2px_rgba(0,0,0,0.04),0_8px_24px_-12px_rgba(20,30,26,0.18)]">
      <div className="relative px-5 pb-3.5 pt-4">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-theme-primary to-theme-accent-warm" />
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-[17px] font-extrabold tracking-tight text-theme-fg">Review Contacts</h2>
              <span className="inline-flex items-center rounded-full bg-theme-primary/12 px-2.5 py-0.5 text-[10px] font-extrabold uppercase tracking-wider text-theme-primary">
                {contacts.length} {contacts.length === 1 ? "CONTACT" : "CONTACTS"}
              </span>
            </div>
            <p className="mt-1 text-xs font-medium text-theme-fg-muted">
              Fix typos, drop bad entries, and confirm the tag before anything writes to GHL
            </p>
          </div>
          <button
            type="button"
            onClick={() => setIsCollapsed(true)}
            className="flex h-[30px] w-[30px] flex-shrink-0 items-center justify-center rounded-[10px] text-theme-fg-muted hover:bg-theme-background"
            title="Collapse"
            aria-label="Collapse contact review"
          >
            <span className="material-icons-outlined text-[18px]">unfold_less</span>
          </button>
        </div>
        <div className="mt-3.5 flex flex-wrap gap-1.5">
          <span className="inline-flex items-center gap-1.5 rounded-[10px] border border-theme-primary/30 bg-theme-background px-2.5 py-1.5 text-[11px] font-bold text-theme-primary">
            <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-theme-primary" />
            <b className="text-[12px] font-extrabold">{batch.summary.ready}</b>&nbsp;Ready
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-[10px] border border-theme-border bg-theme-background px-2.5 py-1.5 text-[11px] font-bold text-theme-fg-muted">
            <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-theme-fg-muted opacity-50" />
            <b className="text-[12px] font-extrabold text-theme-fg">{batch.summary.already_in_crm}</b>&nbsp;Already in GHL
          </span>
          <span
            className="inline-flex items-center gap-1.5 rounded-[10px] border px-2.5 py-1.5 text-[11px] font-bold"
            style={{
              borderColor: "color-mix(in srgb, var(--color-accent-warm) 45%, var(--color-border))",
              background: "color-mix(in srgb, var(--color-accent-warm) 8%, var(--color-surface))",
              color: "color-mix(in srgb, var(--color-accent-warm) 65%, var(--color-text-primary) 35%)",
            }}
          >
            <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-theme-accent-warm" />
            <b className="text-[12px] font-extrabold">{batch.summary.needs_review}</b>&nbsp;Need review
          </span>
          {duplicateCount > 0 && (
            <span className="inline-flex items-center gap-1.5 rounded-[10px] border border-theme-border bg-theme-background px-2.5 py-1.5 text-[11px] font-bold text-theme-fg-muted">
              <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-theme-fg-muted opacity-35" />
              <b className="text-[12px] font-extrabold text-theme-fg">{duplicateCount}</b>&nbsp;Merged
            </span>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between border-y border-theme-border bg-theme-background/60 px-5 py-2.5 text-[12px] text-theme-fg-muted">
        <label className="flex cursor-pointer items-center gap-2 font-bold text-theme-fg">
          <input
            type="checkbox"
            className="h-3.5 w-3.5 accent-theme-primary"
            checked={allSelected}
            onChange={toggleSelectAll}
            disabled={selectableIndices.length === 0}
            aria-label="Select all"
          />
          Select all
        </label>
        <span className="flex items-center gap-3">
          <span>{selectedIndices.length} of {selectableIndices.length} selected</span>
          {deletedCount > 0 && <span>{deletedCount} removed</span>}
          {blockedCount > 0 && (
            <span className="inline-flex items-center gap-1 text-theme-danger">
              <span className="material-icons-outlined text-[13px]">block</span>
              {blockedCount} need an email or phone to import
            </span>
          )}
        </span>
      </div>

      <div className="flex flex-col gap-1.5 overflow-y-auto p-3" style={{ maxHeight: 400 }}>
        <div className={`grid ${ROW_GRID} gap-3 px-3.5 pb-1 text-[9.5px] font-extrabold uppercase tracking-wide text-theme-fg-muted`}>
          <span />
          <span>Contact</span>
          <span>Status</span>
        </div>

        {contacts.map((contact, i) => {
          if (contact.status === "duplicate_in_batch") return null;

          const isEditing = editingIndex === i;
          const isDeleted = deleted.has(i);
          const isUpserted = !!contact.upserted;
          const canSelect = !contact.issues.includes("no_email_or_phone");
          const rowIssue = contact.issues[0] ? issueBadge[contact.issues[0]] : null;
          // A data-quality issue (invalid_email/invalid_phone) always outranks
          // "already in GHL" — that status is informational, but importing a
          // row like this would silently overwrite a real GHL value (e.g. a
          // valid phone) with garbage, so the row must never look clean.
          const rowTone: "ok" | "crm" | "warn" | "danger" | null =
            contact.issues.some((iss) => iss === "invalid_email" || iss === "invalid_phone")
              ? "danger"
              : contact.status === "already_in_crm"
              ? "crm"
              : contact.issues.length > 0
              ? "warn"
              : contact.upsert_error
              ? "danger"
              : contact.status === "ok"
              ? "ok"
              : null;

          if (isDeleted) {
            return (
              <div
                key={i}
                className={`grid ${COMPACT_GRID} items-center gap-3 rounded-[13px] border border-theme-border bg-theme-surface px-3.5 py-2.5 opacity-50`}
              >
                <div className="flex h-8 w-8 items-center justify-center rounded-full text-[11px] font-extrabold" style={avatarTint(i)}>
                  {initials(contact.name)}
                </div>
                <div className="min-w-0">
                  <p className="truncate text-[12.5px] text-theme-fg-muted line-through">{contact.name || "(no name)"}</p>
                  <p className="truncate text-[10.5px] text-theme-fg-muted">Removed from import</p>
                </div>
                <button
                  type="button"
                  onClick={() => undoDelete(i)}
                  className="inline-flex items-center gap-1 justify-self-end text-[11.5px] font-bold text-theme-primary"
                >
                  <span className="material-icons-outlined text-[14px]">undo</span>Undo
                </button>
              </div>
            );
          }

          if (isEditing) {
            return (
              <div
                key={i}
                className={`grid ${COMPACT_GRID} items-center gap-3 rounded-[13px] border border-theme-primary bg-theme-surface px-3.5 py-2.5 shadow-[0_0_0_3px_rgba(47,73,65,0.12)]`}
              >
                <div className="flex h-8 w-8 items-center justify-center rounded-full text-[11px] font-extrabold" style={avatarTint(i)}>
                  {initials(contact.name)}
                </div>
                <div className="flex min-w-0 items-center gap-2">
                  <input
                    className="w-[34%] rounded-lg border border-theme-border bg-theme-background px-2.5 py-1.5 text-[12.5px] font-semibold text-theme-fg outline-none focus:border-theme-primary"
                    value={draft.name}
                    onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                    placeholder="Name"
                  />
                  <input
                    className="min-w-0 flex-1 rounded-lg border border-theme-border bg-theme-background px-2.5 py-1.5 text-[12.5px] text-theme-fg outline-none focus:border-theme-primary"
                    value={draft.email}
                    onChange={(e) => setDraft((d) => ({ ...d, email: e.target.value }))}
                    placeholder="Email"
                  />
                  <input
                    className="min-w-0 flex-1 rounded-lg border border-theme-border bg-theme-background px-2.5 py-1.5 text-[12.5px] text-theme-fg outline-none focus:border-theme-primary"
                    value={draft.phone}
                    onChange={(e) => setDraft((d) => ({ ...d, phone: e.target.value }))}
                    placeholder="Phone"
                  />
                </div>
                <div className="flex items-center justify-self-end gap-1">
                  <button
                    type="button"
                    onClick={() => saveEdit(i)}
                    disabled={isSavingEdit}
                    className="flex h-[27px] w-[27px] items-center justify-center rounded-[9px] bg-theme-primary text-white disabled:opacity-60"
                    title="Save"
                  >
                    <span className="material-icons-outlined text-[16px]">check</span>
                  </button>
                  <button
                    type="button"
                    onClick={cancelEdit}
                    disabled={isSavingEdit}
                    className="flex h-[27px] w-[27px] items-center justify-center rounded-[9px] text-theme-fg-muted hover:bg-theme-background"
                    title="Cancel"
                  >
                    <span className="material-icons-outlined text-[16px]">close</span>
                  </button>
                </div>
              </div>
            );
          }

          return (
            <div
              key={i}
              className={`grid ${ROW_GRID} items-center gap-3 rounded-[13px] border border-theme-border bg-theme-surface px-3.5 py-2.5 ${
                isUpserted ? "opacity-55" : ""
              }`}
              style={rowAccentStyle(rowTone)}
            >
              <div className="flex h-8 w-8 items-center justify-center rounded-full text-[11px] font-extrabold" style={avatarTint(i)}>
                {initials(contact.name)}
              </div>

              {/* Name and phone are the two things a person needs to recognize/verify
                  a row at a glance, so they're the full-width primary/secondary
                  lines here; company + email fold into a smaller third line —
                  de-emphasized, not hidden, and always complete on hover via title. */}
              <div
                className="min-w-0"
                title={[contact.name, contact.phone, contact.company, contact.email].filter(Boolean).join(" · ") || undefined}
              >
                <p className="truncate text-[13px] font-bold text-theme-fg">{contact.name || "(no name)"}</p>
                <div
                  className={`flex items-center gap-1 truncate text-[12px] font-semibold ${
                    !contact.phone || contact.issues.includes("invalid_phone") ? "italic text-theme-danger" : "text-theme-fg"
                  }`}
                >
                  <span className="material-icons-outlined flex-shrink-0 text-[12px]">call</span>
                  {contact.phone || "no phone on file"}
                </div>
                <p className={`truncate text-[10.5px] ${!contact.email ? "italic text-theme-danger" : "text-theme-fg-muted"}`}>
                  {[contact.company, contact.email || "no email on file"].filter(Boolean).join(" · ")}
                </p>
              </div>

              {/* Badge + checkbox + edit/delete share one trailing column (rather
                  than two separate grid tracks), stacked instead of side-by-side —
                  an auto-sized grid track fits unwrapped content by default, so
                  a plain side-by-side row here would claim just as much width as
                  two separate tracks did. Stacking keeps this column's footprint
                  to whichever row is wider (badge or icons), handing everything
                  else to the name/phone column. */}
              <div className="flex flex-col items-end justify-self-end gap-1">
                {isUpserted ? (
                  <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[9.5px] font-extrabold uppercase tracking-wide" style={badgeStyle("crm")}>
                    <span className="material-icons-outlined text-[11px]">cloud_done</span>Imported
                  </span>
                ) : rowIssue ? (
                  <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[9.5px] font-extrabold uppercase tracking-wide" style={badgeStyle(rowIssue.tone)}>
                    <span className="material-icons-outlined text-[11px]">{rowIssue.tone === "danger" ? "error_outline" : "warning"}</span>
                    {rowIssue.label}
                  </span>
                ) : contact.status === "already_in_crm" ? (
                  <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[9.5px] font-extrabold uppercase tracking-wide" style={badgeStyle("crm")}>
                    <span className="material-icons-outlined text-[11px]">check_circle</span>Already in GHL
                  </span>
                ) : contact.upsert_error ? (
                  <span
                    className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[9.5px] font-extrabold uppercase tracking-wide"
                    style={badgeStyle("danger")}
                    title={upsertErrorLabel(contact.upsert_error)}
                  >
                    <span className="material-icons-outlined text-[11px]">error_outline</span>
                    {upsertErrorLabel(contact.upsert_error)}
                  </span>
                ) : contact.status === "ok" ? (
                  <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[9.5px] font-extrabold uppercase tracking-wide" style={badgeStyle("ok")}>
                    <span className="material-icons-outlined text-[11px]">check</span>Ready
                  </span>
                ) : null}

                <div className="flex items-center gap-0.5">
                  {canSelect ? (
                    <input
                      type="checkbox"
                      className="mr-1 h-3.5 w-3.5 flex-shrink-0 accent-theme-primary disabled:opacity-50"
                      checked={!unchecked.has(i)}
                      onChange={() => toggleChecked(i)}
                      disabled={isImporting}
                      aria-label={`Include ${contact.name || "this contact"}`}
                    />
                  ) : (
                    <span
                      className="material-icons-outlined mr-1 flex-shrink-0 text-[15px] text-theme-danger"
                      title="Add an email or phone before this contact can be imported"
                    >
                      block
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => startEdit(i)}
                    disabled={isImporting}
                    className="flex h-[27px] w-[27px] items-center justify-center rounded-[9px] text-theme-fg-muted hover:bg-theme-background hover:text-theme-fg disabled:pointer-events-none disabled:opacity-40"
                    title="Edit"
                  >
                    <span className="material-icons-outlined text-[16px]">edit</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => deleteRow(i)}
                    disabled={isImporting}
                    className="flex h-[27px] w-[27px] items-center justify-center rounded-[9px] text-theme-fg-muted hover:bg-theme-background hover:text-theme-danger disabled:pointer-events-none disabled:opacity-40"
                    title="Remove"
                  >
                    <span className="material-icons-outlined text-[16px]">delete_outline</span>
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="border-t border-theme-border px-5 py-3.5">
        <p className="mb-2 flex items-center gap-1.5 text-[10.5px] font-extrabold uppercase tracking-wide text-theme-fg-muted">
          <span className="material-icons-outlined text-[13px]">local_offer</span>
          Tag to apply to all imported contacts
        </p>
        <div className="flex flex-wrap items-center gap-1.5">
          {tags.map((tag) => (
            <span key={tag} className="inline-flex items-center gap-1.5 rounded-full bg-theme-primary py-1.5 pl-3 pr-1.5 text-[11.5px] font-bold text-white">
              {tag}
              <button
                type="button"
                onClick={() => removeTag(tag)}
                disabled={isImporting}
                className="flex h-3.5 w-3.5 items-center justify-center opacity-85 disabled:pointer-events-none disabled:opacity-40"
                aria-label={`Remove tag ${tag}`}
              >
                <span className="material-icons-outlined text-[13px]">close</span>
              </button>
            </span>
          ))}
          <input
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addTag();
              }
            }}
            onBlur={addTag}
            disabled={isImporting}
            placeholder="Add tag&hellip;"
            className="rounded-full border border-dashed border-theme-border bg-transparent px-3 py-1.5 text-[11.5px] font-medium text-theme-fg outline-none focus:border-theme-primary disabled:opacity-50"
          />
        </div>
      </div>

      {importError && (
        <div className="border-t border-theme-border px-5 py-2.5 text-[12px] font-medium text-theme-danger">{importError}</div>
      )}

      <div className="flex items-center justify-between gap-3 border-t border-theme-border bg-theme-background/45 px-5 py-3.5">
        <p className="text-[11.5px] text-theme-fg-muted">
          <b className="font-extrabold text-theme-fg">{selectedIndices.length}</b> of {selectableIndices.length} selected
        </p>
        <div className="flex flex-shrink-0 gap-2">
          <button
            type="button"
            onClick={() => setIsCollapsed(true)}
            disabled={isImporting}
            className="rounded-[10px] border border-theme-border px-3.5 py-2 text-[12.5px] font-bold text-theme-fg hover:bg-theme-background disabled:cursor-not-allowed disabled:opacity-50"
          >
            Dismiss
          </button>
          <button
            type="button"
            onClick={() => onImport(selectedIndices, tags)}
            disabled={isImporting || selectedIndices.length === 0}
            className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-[10px] bg-theme-primary px-3.5 py-2 text-[12.5px] font-bold text-white shadow-[0_4px_10px_-4px_rgba(47,73,65,0.6)] transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isImporting ? (
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
            ) : (
              <span className="material-icons-outlined text-[15px]">cloud_upload</span>
            )}
            {isImporting
              ? "Importing…"
              : `Import ${selectedIndices.length} ${selectedIndices.length === 1 ? "Contact" : "Contacts"} to GHL`}
          </button>
        </div>
      </div>
      <p className="px-5 pb-3 text-right text-[10px] text-theme-fg-muted">
        Existing GHL contacts are matched by email and updated, not duplicated.
      </p>
    </div>
  );
};
